import { forwardRef, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { HERO_SCREENS, type HeroHandoff } from './heroLayout';
import './hero.css';

// The welcome hero's floating "screens": REAL engine output from
// public/promo (2026-09-24). Every clip and poster here is ours; nothing is
// invented. The clips are all vertical (360x624-ish), so the screens are too.
//
// These DOM stills are three things at once:
//   * the layout — hero.css places them; the WebGL scene reads their boxes
//     back and puts a 3D twin exactly on each one (readScreenAnchors),
//   * the first paint — they show before three.js has even downloaded,
//   * the fallback — no WebGL or reduced motion keeps them for good.
// Decorative only (aria-hidden): the headline and the tool cards say what
// they show.

// Where each screen takes you on the home page (owner 2026-10-19: "if a user clicks on them they should bring or
// scroll them to the corresponding tool"): its card below; on any other page, or if the card is missing, its page.
const CARD_FOR: Record<string, string> = {
  corrido: 'tool-card-corrido', story: 'tool-card-story', ad: 'tool-card-ads', canvas: 'home-spotify-loop',
  storybook: 'tool-card-storybook', editor: 'tool-card-chopbox',
  // corrido3d and facelock have no card on the home page: their links open the 3D music video and the avatars.
};
function scrollToTool(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  const el = document.querySelector<HTMLElement>(`[data-testid="${CARD_FOR[id] || ''}"]`);
  if (!el) return; // the link opens the tool's page
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export const HeroScreens = forwardRef<HTMLDivElement, { handoff: HeroHandoff }>(function HeroScreens({ handoff }, ref) {
  return (
    <div ref={ref} className="hero-screens" data-handoff={handoff} data-testid="hero-screens">
      <div className="hero-screens__row">
        {HERO_SCREENS.map((s, i) => (
          <figure
            key={s.id}
            data-hero-screen={s.id}
            className="hero-screen m-0"
            style={{
              '--ox': s.ox, '--hs': s.hs, '--ar': s.aspect, '--z': s.z, '--depth': s.depth, '--ry': s.ry, '--rz': s.rz, '--dim': s.dim,
              '--lx': s.lx, ...(s.lxs ? { '--lxs': s.lxs } : {}), '--ly': s.ly, '--lh': s.lh, '--ldepth': s.ldepth, '--lry': s.lry, '--lrz': s.lrz, '--ldim': s.ldim,
              '--i': i,
            } as CSSProperties}
          >
            {/* Each screen opens its tool (owner 2026-10-19); it stays clickable after the 3D twin takes over the look. */}
            <Link to={s.to} aria-label={s.label} title={s.label} className="hero-screen__float hero-screen__link" onClick={(e) => { trackEvent('hero_screen_clicked', { screen: s.id }); scrollToTool(e, s.id); }}>
              <div className="hero-screen__card">
                <img src={s.poster} alt="" draggable={false} decoding="async" />
              </div>
            </Link>
          </figure>
        ))}
      </div>
    </div>
  );
});

